/**
 * PF Cart Availability — live estimated ship times for the cart.
 *
 * Replaces the FulfillmentEstimate AppScript on the cart page and the cart
 * drawer. One POST covers the whole cart: every line is quoted at the quantity
 * ordered and the order total is the longest of them.
 *
 * Fills, anywhere in the document:
 *   [data-pf-line-ship-time][data-sku]  per line item
 *   [data-pf-order-ship-time]           the order as a whole
 *
 * Attributes (set by pf-cart-availability.liquid):
 *   data-availability-endpoint  URL of the lines availability endpoint.
 */

import { localizeEndpoint } from './pf-variant-picker/engine/api-base.js';
import { availabilityString } from './pf-variant-picker/engine/availability-view.js';

if (!customElements.get('pf-cart-availability')) {
  class PfCartAvailability extends HTMLElement {
    connectedCallback() {
      this._boundOnChange = () => this._fetch();
      document.addEventListener('on:line-item:change', this._boundOnChange);
      this._fetch();
    }

    disconnectedCallback() {
      this._abort?.abort();
      document.removeEventListener('on:line-item:change', this._boundOnChange);
    }

    _slots() {
      return [
        ...document.querySelectorAll('[data-pf-line-ship-time][data-sku]'),
      ];
    }

    _lines() {
      const bySku = new Map();
      for (const slot of this._slots()) {
        const sku = slot.dataset.sku;
        const quantity = parseInt(slot.dataset.quantity, 10) || 1;
        bySku.set(sku, (bySku.get(sku) ?? 0) + quantity);
      }
      return [...bySku].map(([sku, quantity]) => ({ sku, quantity }));
    }

    _fetch() {
      const url = localizeEndpoint(this.dataset.availabilityEndpoint);
      const lines = this._lines();
      if (!url || lines.length === 0) return;

      this._abort?.abort();
      const controller = new AbortController();
      this._abort = controller;

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(lines),
        mode: 'cors',
        credentials: 'omit',
        // Availability is live inventory — never reuse a stale cached copy.
        cache: 'no-store',
        signal: controller.signal,
      })
        .then((res) => {
          if (!res.ok)
            throw new Error(`Availability fetch failed: ${res.status}`);
          return res.json();
        })
        .then((payload) => {
          if (this._abort !== controller || !this.isConnected) return;
          this._render(payload);
        })
        .catch((err) => {
          if (controller.signal.aborted || this._abort !== controller) return;
          // Degrade quietly — the cart itself stays functional without a date.
          console.warn('[pf-cart-availability] fetch failed:', err?.message);
        });
    }

    _render(payload) {
      const bySku = new Map();
      for (const line of payload?.lines ?? []) {
        if (line?.sku) bySku.set(line.sku, line);
      }
      for (const slot of this._slots()) {
        fill(slot, bySku.get(slot.dataset.sku)?.estimatedShipTime);
      }
      for (const slot of document.querySelectorAll(
        '[data-pf-line-availability][data-sku]'
      )) {
        fill(slot, availabilityString(bySku.get(slot.dataset.sku)?.available));
      }
      for (const slot of document.querySelectorAll(
        '[data-pf-order-ship-time]'
      )) {
        fill(slot, payload?.order?.estimatedShipTime);
      }
    }
  }

  const fill = (slot, shipTime) => {
    slot.textContent = shipTime ?? '';
    slot.hidden = !shipTime;
  };

  customElements.define('pf-cart-availability', PfCartAvailability);
}
